import { cn } from "@/lib/utils";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import {
  Label as ProjectLabel,
  PreAnnotateModelTypeEnum,
  PreAnnotateSettingsDocument,
  UpdatePreAnnotateSettingsDocument,
} from "@repo/schema";
import { useTranslation } from "react-i18next";
import { PreAnnotateRunOutcome } from "../../types/preAnnotate";
import type { GenerateLabelDescription } from "../GenerateDescriptionButton";
import {
  DIALOG_TITLE,
  FOOTER_CANCEL,
  FOOTER_SAVE,
  FOOTER_SAVING,
  SAVE_FAILED,
  VISION_SEGMENTATION_SOON,
} from "./copy";
import { DefaultModeToggle } from "./DefaultModeToggle";
import { LabelDescriptionsSection } from "./LabelDescriptionsSection";
import { isVisionAvailable } from "./preAnnotateSettingsForm";
import { ProjectNotesSection } from "./ProjectNotesSection";
import { RunOutcomeBanner } from "./RunOutcomeBanner";
import { usePreAnnotateSettingsForm } from "./usePreAnnotateSettingsForm";
import {
  VISION_MUTED_CLASS,
  VisionUnavailableNotice,
} from "./VisionUnavailableNotice";
import { apiErrorMessage } from "@/core/api";

export interface PreAnnotateSettingsDialogProps {
  /** Undefined only before the active project resolves. */
  projectId: number | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The aggregate GET; undefined while loading or when the request failed. */
  settings?: PreAnnotateSettingsDocument;
  isLoadingSettings: boolean;
  /**
   * The PROJECT's labels. Robopipe Vision has no model, so its descriptions
   * are keyed by project label.
   */
  projectLabels: ProjectLabel[];
  /** Outcome of the last auto-annotation of the OPEN task, or null. */
  lastRun: PreAnnotateRunOutcome | null;
  /** Rejects to keep the dialog open with an inline error. */
  onSave: (doc: UpdatePreAnnotateSettingsDocument) => Promise<void>;
  isSaving: boolean;
  /** Select an image on the label page — a project note's "Open image". */
  onOpenTask: (taskId: number) => void;
  /** The ✨ beside each description; shown only while Vision is available. */
  onGenerateDescription?: GenerateLabelDescription;
}

/**
 * Thin scrollbar for the body, matching the rest of the label page. Only the
 * body scrolls: header and footer stay put so Save is always reachable.
 */
const SCROLL_BODY_CLASS =
  "flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 pt-6 pb-2 [scrollbar-color:rgba(0,0,0,0.15)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb:hover]:bg-black/25 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-black/15 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5";

export const PreAnnotateSettingsDialog = ({
  projectId,
  open,
  onOpenChange,
  settings,
  isLoadingSettings,
  projectLabels,
  lastRun,
  onSave,
  isSaving,
  onOpenTask,
  onGenerateDescription,
}: PreAnnotateSettingsDialogProps) => {
  const { t } = useTranslation(["label", "common"]);
  const controlsDisabled = isSaving || isLoadingSettings;
  const { state, dispatch, document, saveGate } = usePreAnnotateSettingsForm({
    open,
    settings,
    projectLabels,
    busy: controlsDisabled,
  });

  // Availability comes from configuration, not a health probe, so it is known
  // as soon as the document is. Until then nothing is shown either way.
  const visionAvailable = isVisionAvailable(settings?.availability);
  const showVisionNotice = !isLoadingSettings && !visionAvailable;
  const visionMuted = !visionAvailable;

  const handleSave = async () => {
    dispatch({ type: "clearError" });
    try {
      await onSave(document);
      onOpenChange(false);
    } catch (err) {
      dispatch({
        type: "saveFailed",
        message: apiErrorMessage(err, t(SAVE_FAILED)),
      });
    }
  };

  const modeToggle = (
    <DefaultModeToggle
      value={state.defaultMode}
      onChange={(mode) => dispatch({ type: "setDefaultMode", mode })}
      disabled={controlsDisabled}
      // Vision detects boxes only; the form coerces the mode to Detection, so
      // the toggle just has to say why the other half is out.
      disabledReasons={{
        [PreAnnotateModelTypeEnum.SEGMENTATION]: t(VISION_SEGMENTATION_SOON),
      }}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100vh-64px)] flex-col gap-0 p-0 sm:max-w-[720px]">
        <DialogHeader className="shrink-0 border-b px-8 pt-6 pb-4">
          <DialogTitle className="text-xl font-semibold">
            {t(DIALOG_TITLE)}
          </DialogTitle>
        </DialogHeader>

        <div className={SCROLL_BODY_CLASS}>
          {lastRun && <RunOutcomeBanner outcome={lastRun} />}
          {showVisionNotice && <VisionUnavailableNotice />}

          <div
            className={cn("flex flex-col gap-6", visionMuted && VISION_MUTED_CLASS)}
            inert={visionMuted}
          >
            {modeToggle}
            <LabelDescriptionsSection
              labels={projectLabels}
              descriptions={state.labelDescriptions}
              onChange={(labelId, value) =>
                dispatch({ type: "setDescription", labelId, value })
              }
              disabled={controlsDisabled}
              onGenerate={visionAvailable ? onGenerateDescription : undefined}
            />
            {projectId !== undefined && (
              <ProjectNotesSection
                projectId={projectId}
                onOpenTask={(taskId) => {
                  onOpenChange(false);
                  onOpenTask(taskId);
                }}
                disabled={isSaving}
              />
            )}
          </div>
        </div>

        {state.saveError && (
          <p className="shrink-0 px-8 pt-2 text-sm text-destructive">
            {state.saveError}
          </p>
        )}

        <DialogFooter className="shrink-0 border-t px-8 py-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {t(FOOTER_CANCEL)}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!saveGate.ok}
            title={saveGate.reason && t(saveGate.reason)}
          >
            {isSaving ? t(FOOTER_SAVING) : t(FOOTER_SAVE)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
